import { useState } from "react";
import { Mail, MessageCircle, Pencil, CheckCircle2, Truck, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { ConfirmDelete } from "@/components/admin/AdminUi";
import { StatePill, rowStyle, fillStyle } from "@/lib/orderStatus";
import { STATUS, itDate, waLink, daysSince, daysText } from "./utils";

async function notifyCustomer(o, onDone) {
  const win = o.phone ? window.open("", "_blank") : null;
  try {
    const { data } = await api.post(`/admin/counter-orders/${o.id}/notify`);
    if (win) win.location.href = waLink(o.phone, data.message);
    toast.success(data.email_sent ? `Email inviata a ${o.email}${o.phone ? " · WhatsApp aperto con il messaggio" : ""}` : "WhatsApp aperto con il messaggio pronto");
    onDone?.();
  } catch (e) { win?.close(); toast.error(errMsg(e)); }
}

export const NEXT = { sospeso: ["lavorazione", "Segna ordinato", Truck], lavorazione: ["completato", "Ritirato", CheckCircle2], completato: ["sospeso", "Riapri", RotateCcw] };
export const ROW_GRID = "xl:grid xl:grid-cols-[128px_92px_92px_minmax(0,1fr)_minmax(0,1.3fr)_80px_176px] xl:gap-3 xl:items-start";

function OnlineInfo({ o, onDone }) {
  const send = async () => {
    try { const { data } = await api.post(`/admin/counter-orders/${o.id}/sendcloud`); toast.success(`Spedizione creata su Sendcloud (#${data.parcel_id})`); onDone?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  const s = o.shipping;
  return (
    <div className="rounded-md bg-white/80 border border-sky-300 p-2 text-sm space-y-1" data-testid={`counter-online-${o.id}`}>
      <div className="flex flex-wrap gap-1.5 items-center">
        <span className="px-1.5 py-0.5 rounded bg-sky-700 text-white font-semibold text-xs">ONLINE</span>
        <span className={`px-1.5 py-0.5 rounded font-semibold text-xs ${o.paid ? "bg-green-700 text-white" : "bg-amber-200 text-amber-950"}`} data-testid={`counter-paid-${o.id}`}>{o.paid ? "PAGATO" : "DA PAGARE AL RITIRO"}</span>
        <span>{o.fulfillment === "ship" ? "Spedizione" : "Ritiro in negozio"}</span>
      </div>
      {s && <div>{s.name} · {s.address}, {s.zip} {s.city} {s.province}</div>}
      {s && (o.sendcloud?.parcel_id
        ? <div className="text-green-800 font-semibold" data-testid={`counter-sendcloud-done-${o.id}`}>Su Sendcloud #{o.sendcloud.parcel_id} {o.sendcloud.tracking_number && `· ${o.sendcloud.tracking_number}`}</div>
        : <button data-testid={`counter-sendcloud-${o.id}`} onClick={send} className="press h-8 px-2 rounded bg-brand-navy text-white font-semibold text-xs">Importa su Sendcloud</button>)}
    </div>
  );
}

const lbl = "xl:hidden font-mono text-[11px] uppercase text-slate-600 mr-1";

export function OrderCard({ o, cfg, onStatus, onEdit, onDelete, onNotified, row, fx = "", done }) {
  const [open, setOpen] = useState(false);
  const notify = (x) => notifyCustomer(x, onNotified);
  const [next, label, Icon] = NEXT[o.status];
  const waiting = o.status === "sospeso" ? daysSince(o.date) : null;
  const num = o.booking_number || `#${o.id.slice(-5).toUpperCase()}`;
  return (
    <div style={rowStyle(o.status)} className={`ord-row px-4 py-3.5 border-b-2 border-white text-base text-slate-900 space-y-2 ${row ? `${ROW_GRID} xl:space-y-0` : ""} ${done ? "opacity-60" : ""} ${fx}`} data-testid={`counter-order-${o.id}`}>
      <div className="flex flex-wrap items-center gap-2"><StatePill s={o.status} label={STATUS[o.status].short} testid={`counter-order-status-${o.id}`} /></div>
      <div className="font-mono text-sm text-slate-700"><span className={lbl}>N°</span>{num}</div>
      <div className="font-mono text-sm text-slate-700"><span className={lbl}>Data</span>{itDate(o.date)}
        {waiting !== null && <div className={`text-xs ${waiting > cfg.threshold_days ? "text-red-800 font-bold" : "text-slate-700"}`}>in attesa da {daysText(waiting)}</div>}
      </div>
      <div className="min-w-0">
        <div className="text-lg font-bold leading-tight break-words" data-testid={`counter-order-name-${o.id}`}>{o.name}</div>
        {o.phone && <div className="text-sm text-slate-700">{o.phone}</div>}
        {o.email && <div className="text-sm text-slate-700 truncate" title={o.email}>{o.email}</div>}
        {o.notified_at && <div className="text-xs text-green-800 font-semibold" data-testid={`counter-notified-${o.id}`}>Avvisato il {new Date(o.notified_at).toLocaleString("it-IT", { dateStyle: "short", timeStyle: "short" })}</div>}
      </div>
      <div className="min-w-0 space-y-1">
        <button type="button" onClick={() => setOpen(!open)} data-testid={`counter-order-item-${o.id}`} title={open ? "Mostra meno" : "Mostra tutto"} className={`text-left break-words w-full ${open ? "" : "line-clamp-2"}`}>{o.item}</button>
        {o.code && <div className="text-sm text-slate-700" data-testid={`counter-order-code-${o.id}`}>Cod. <b className="font-mono">{o.code}</b></div>}
        {o.notes && <div className="text-sm text-slate-700 italic">{o.notes}</div>}
        {o.source === "online" && <OnlineInfo o={o} onDone={onNotified} />}
      </div>
      <div className="font-semibold"><span className={lbl}>Totale</span>{o.total ? `€ ${Number(o.total).toFixed(2)}` : "—"}</div>
      <div className="flex flex-wrap gap-2">
        <button data-testid={`counter-next-${o.id}`} onClick={() => onStatus(o, next)} style={fillStyle(next)} className="press h-9 px-3 rounded-md text-sm font-bold flex items-center gap-1.5"><Icon className="w-4 h-4" /> {label}</button>
        {(o.phone || o.email) && (
          <button data-testid={`counter-notify-${o.id}`} onClick={() => notify(o)} title={[o.email && `Email a ${o.email}`, o.phone && `WhatsApp a ${o.phone}`].filter(Boolean).join(" + ")}
            className="press h-9 px-3 rounded-md bg-[#128C4A] text-white text-sm font-semibold flex items-center gap-1.5">
            {o.email && <Mail className="w-4 h-4" />}{o.phone && <MessageCircle className="w-4 h-4" />} Avvisa
          </button>
        )}
        <button data-testid={`counter-edit-${o.id}`} onClick={() => onEdit(o)} className="press h-9 px-3 rounded-md border border-slate-400 bg-white text-sm font-semibold flex items-center gap-1.5"><Pencil className="w-4 h-4" /> Modifica</button>
        <ConfirmDelete small testId={`counter-delete-${o.id}`} title={`Eliminare l'ordine di ${o.name}?`} text="L'ordine verrà cancellato definitivamente." onConfirm={() => onDelete(o)} />
      </div>
    </div>
  );
}
